import * as lodash from "lodash";
import { AfterViewInit, Component, OnDestroy, OnInit } from "@angular/core";
import { Location } from "@angular/common";
import { Event, NavigationEnd, NavigationError, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subscription } from "rxjs";

import {
    HTTP_COOKIES,
    ITCIAppCustomizationParam,
    ITCIEnvVars,
    TCI_APP_CUSTOMIZATION_TYPE,
    TCI_MAIN_MENU_TYPE,
    TCIMainMenu,
    NavBarNotificationType
} from "@tci/common";
import { NgEnvVarsService } from "../ng-shared/ng-services/ng-env-vars.service";
import { TciTenantService } from "../ng-tenant/tci-tenant.service";
import { ITenantMap, TciTenantModelService } from "../ng-tenant/tci-tenant.model";
import { NgToastrService } from "../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { NgUserService } from "../ng-shared/ng-services/ng-user.service";
import { NgCustomizationProviderService } from "../ng-shared/ng-services/ng-customization-provider.service";
import { CookieService } from "angular2-cookie/core";
import { NgOrgChangeNotificationService } from "../ng-shared/ng-services/ng-org-change-notification.service";

declare var GlobalNavbar: any;

@Component({
    selector: "ng-tci-header",
    templateUrl: "./ng-header-tci.component.html",
    styleUrls: ["./ng-header-tci.component.less"]
})
export class NgHeaderTciComponent implements OnInit, OnDestroy {

    private tenantMap: ITenantMap = {};
    private navBar;
    private helpPanelShown = false;
    private selectedMainMenuItem: TCIMainMenu = null;
    private mainMenuItems = TCIMainMenu.getAllItems((data) => data.getOptionType() === TCI_MAIN_MENU_TYPE.MAIN_MENU);
    private hamburgerMenuItems = TCIMainMenu.getAllItems((data) => data.getOptionType() ===
        TCI_MAIN_MENU_TYPE.HAMBURGER_MENU);
    private globalNavbarItems = TCIMainMenu.getAllItems((data) => data.getOptionType() ===
        TCI_MAIN_MENU_TYPE.GLOBAL_NAV_BAR);
    private vpnConnectionEnabled = false;
    private menuItemsToDisplay: TCIMainMenu[] = [];

    private hamburgerMenuItems$: Observable<TCIMainMenu[]> = Observable.of([]);
    private routerEventsSubscription: Subscription;

    // Custom Configs
    private CUSTOM_TYPES = TCI_APP_CUSTOMIZATION_TYPE;
    private customConfig: { [key: string]: ITCIAppCustomizationParam } = {};

    constructor(
        private envVars: NgEnvVarsService,
        private location: Location,
        private router: Router,
        private tenantSvc: TciTenantService,
        private tenantModelSvc: TciTenantModelService,
        private translate: TranslateService,
        private toastr: NgToastrService,
        private userService: NgUserService,
        private customizationProviderService: NgCustomizationProviderService,
        private cookieService: CookieService,
        private orgChangeSvc: NgOrgChangeNotificationService
    ) {
        this.watchForRouteChange();
        const envVarName = "VPN_ENABLED";

        this
            .envVars
            .getEnvVar(envVarName)
            .subscribe(
                (_envVars: ITCIEnvVars<any>) => {
                    this.vpnConnectionEnabled = _envVars[envVarName] as boolean;
                },
                error => {
                    console.log(error.message);
                }
            );
        this
            .tenantSvc
            .onInit()
            .subscribe(() => {
                this.tenantMap = this.tenantModelSvc.getTenants();
                this.getMenuItemsToDisplay();
            });

        this
            .getCustomConfig()
            .subscribe(() => {
                this.checkNavBars();
                this.evaluateCustomMenuItems();
                this.getMenuItemsToDisplay();
                this.setSelectedMainMenuItem(
                    this
                        .location
                        .path(false)
                        .replace(/^\//, "")
                );
            });
    }

    ngOnInit(): void { }

    ngOnDestroy(): void {
        if (this.routerEventsSubscription) {
            this.routerEventsSubscription.unsubscribe();
        }
    }

    // ngAfterViewInit(): void {
    //     const translate = this.translate;
    //     const toastr = this.toastr;

    //     /* End initializing GNH */
    //     Observable
    //         .of(...this
    //             .mainMenuItems
    //             .sort((a, b) => {
    //                 if (a.getPosition() > b.getPosition()) {
    //                     return 1;
    //                 } else if (a.getPosition() < b.getPosition()) {
    //                     return -1;
    //                 } else {
    //                     return 0;
    //                 }
    //             }))
    //         .take(1)
    //         .subscribe(item => {
    //             // this.router.navigate([item.getRouteId()]);
    //         });
    // }

    // Evaluate menu items allowance
    private evaluateCustomMenuItems() {
        const toRemove: TCIMainMenu[] = [];

        // Marketplace
        let extensionCustom = this.customConfig[this.CUSTOM_TYPES.APP_MAIN_MENU_MARKETPLACE];
        if (extensionCustom && extensionCustom.enabled && !extensionCustom.visible) { // Remove the item
            toRemove.push(TCIMainMenu.MAIN_MENU_MARKETPLACE_V2);
        }

        // Extensions
        extensionCustom = this.customConfig[this.CUSTOM_TYPES.APP_MAIN_MENU_EXTENSIONS];
        if (extensionCustom && extensionCustom.enabled && !extensionCustom.visible) { // Remove the item
            toRemove.push(TCIMainMenu.MAIN_MENU_EXTENSIONS);
        }

        // Env&Tools
        extensionCustom = this.customConfig[this.CUSTOM_TYPES.APP_MAIN_MENU_ENV_TOOLS];
        if (extensionCustom && extensionCustom.enabled && !extensionCustom.visible) { // Remove the item
            toRemove.push(TCIMainMenu.MAIN_MENU_ENV_TOOLS);
        }

        if (toRemove.length > 0) {
            lodash.remove(this.mainMenuItems, mItem => toRemove.includes(mItem));
        }
    }

    private getMenuItemsToDisplay(): void {
        this
            .checkMenuTenantAvailability(this.mainMenuItems)
            .subscribe(
                (menuItems: TCIMainMenu[]) => {
                    this.menuItemsToDisplay = menuItems;
                }
            );
    }

    private checkNavBars() {
        if (this.canShowGlobalNavBar) {
            this.helpPanelShown = true;
            /* Start initializing GNH */
            // https://confluence.tibco.com/pages/viewpage.action?spaceKey=TROP&title=TSC+Universal+Header+Library
            this.navBar = new GlobalNavbar({
                container: "#tci-header",
                textAfterLogo: "Integration",
                // rebrandConfig: {
                //     // IPAS-20054 - Remove the specification of Tibco Cloud icon from TCI webserver
                //     "logo": {
                //         "src": "/assets/img/tibco-cloud-integration-logo.svg"
                //     }
                // },
                iconMenus: {
                    search: {
                        visible: false
                    },
                    help: {
                        visible: true
                    },
                    products: {
                        visible: true
                    },
                    notifications: {
                        visible: false
                    },
                    profile: {
                        visible: true
                    }
                },
                accountChange: {
                    secureHomeURL: "/applications"
                },
                // enforceAuthURL: "/applications",
                locale: "en"
            });
            this.navBar.load();

            /**
             * CONTACT_SUPPORT_STATUS is not used anymore
             */
            // this.navBar.subscribeEvent("CONTACT_SUPPORT_STATUS", response => {
            //     if (response.data && response.data.id === "success") {
            //         translate
            //             .get("CONTACT_SUPPORT_STATUS.success")
            //             .subscribe((resp: string) => {
            //                 toastr.success(resp);
            //             });
            //     } else if (response.data && response.data.id === "error") {
            //         translate
            //             .get("CONTACT_SUPPORT_STATUS.error")
            //             .subscribe((resp: string) => {
            //                 toastr.error(resp);
            //             });
            //     }
            // });
            // There is no #helpPanelDropdownMenu in any template => no dom element => no customization => dead code
            this.navBar.customizePanel("help", (<any>document).querySelector("#helpPanelDropdownMenu"));
            // https://jira.tibco.com/browse/IPAS-13083
            // tslint:disable:max-line-length
            // https://confluence.tibco.com/pages/viewpage.action?spaceKey=UT&title=Usage%3A+Global+Navigation+Header#Usage:GlobalNavigationHeader-Events
            /**
             * When app list aggregated for Scribe and TCI apps what is common is the TSC  JWT , which is used on
             * scribe
             * session creation . The session creation returns a Scribe OrgId and this is cached on a encrypted browser
             * cookie from WS. So If I understand it correctly the scribe OrgId from JTSC WT(Org1) is different from
             * TSC JWT(Org2) which means the scribe cookie (prev orgId) needs to be invalidated on Org Change. This
             * will force the server side to create a new scribe session with new OrgId to be saved in the cookie
             * again. org chang => goes to app list meanwhile the event handler deletes the cookie, the backend when it
             * does not see the cookie will establish a new session with a different cookie with new orgid, so in the
             * end the cookie will be different in the browser( ScribeSession)
             */
            this.orgChangeSvc.setNavbar(this.navBar);
            this.orgChangeSvc.subscribeNavBarNotifications(NavBarNotificationType.ORG_CHANGE)
                .subscribe(() => this.refreshCookieAndMktCache());
            this.orgChangeSvc.subscribeNavBarNotifications(NavBarNotificationType.ORG_CHANGE_IN_OTHER_TAB)
                .subscribe(() => this.refreshCookieAndMktCache());
            this.orgChangeSvc.subscribeNavBarNotifications(NavBarNotificationType.REGION_CHANGE)
                .subscribe(() => this.refreshCookieAndMktCache());
            this.orgChangeSvc.subscribeNavBarNotifications(NavBarNotificationType.SIGN_OUT)
                .subscribe(() => console.log("TCI Sign out."));
        }
    }

    /**
     * IPAS-14988
     * rzavala 20 aug 2020 - Include the marketplace cache flush in the orgChange
     */
    private refreshCookieAndMktCache() {
        // delete cookie will happen on /whoami API call and it
        // will check if the cookie is present before issuing
        // clearcookie call
        // this.userService.deleteCookie(HTTP_COOKIES.ScribeSession);
    }

    public getMainMenuItems(): Observable<TCIMainMenu[]> {
        return this.checkMenuTenantAvailability(this.mainMenuItems);
    }

    public getHamburgerMenuItems(): Observable<TCIMainMenu[]> {
        return this.checkMenuTenantAvailability(this.hamburgerMenuItems);
    }

    public getGlobalNavBarMenuItems(): Observable<TCIMainMenu[]> {
        return this.checkMenuTenantAvailability(this.globalNavbarItems);
    }

    // Customizer
    private getCustomConfig(): Observable<ITCIAppCustomizationParam[]> {
        const appListCustomTypes = [
            this.CUSTOM_TYPES.GLOBAL_NAVBAR,
            this.CUSTOM_TYPES.HELP_PANEL,
            this.CUSTOM_TYPES.APP_MAIN_MENU_MARKETPLACE,
            this.CUSTOM_TYPES.APP_MAIN_MENU_EXTENSIONS,
            this.CUSTOM_TYPES.APP_MAIN_MENU_ENV_TOOLS
        ];

        // Prepare observables to get the config of each type
        const customConfigObs: Observable<ITCIAppCustomizationParam>[] = appListCustomTypes
            .map(customType => {
                return this
                    .customizationProviderService
                    .getCustomizations({
                        type: customType
                    })
                    .map(custom => custom.success ? custom.result : null)
                    .map((customParam: ITCIAppCustomizationParam) => {
                        if (customParam) {
                            this.customConfig[customType] = customParam;
                        }
                        return customParam;
                    });
            });

        // Request the custom configuration
        return Observable
            .merge(...customConfigObs)
            .take(appListCustomTypes.length)
            .toArray();
    }

    public get canShowGlobalNavBar(): boolean {
        const config = this.customConfig[this.CUSTOM_TYPES.GLOBAL_NAVBAR];
        if (!config) { // Default init value
            return true;
        }
        return config.enabled && config.visible && !config.tenantCustomization;
    }

    public get canShowCustomNavBar(): boolean {
        const config = this.customConfig[this.CUSTOM_TYPES.GLOBAL_NAVBAR];
        if (!config) { // Default init value
            return false;
        }
        return config.enabled && config.visible && config.tenantCustomization;
    }

    public get canShowHelpPanel(): boolean {
        const config = this.customConfig[this.CUSTOM_TYPES.HELP_PANEL];
        if (!config) { // Default init value
            return true;
        }
        return config.enabled && config.visible && !config.tenantCustomization;
    }

    public get getLogoUrl(): string {
        const config = this.customConfig[this.CUSTOM_TYPES.GLOBAL_NAVBAR];
        if (config && config.enabled && config.visible && config.tenantCustomization) {
            return config.imageUrl;
        }
        return null;
    }

    public get hasLogoUrl(): boolean {
        return !!this.getLogoUrl;
    }

    public showHideHelpPanel(): void {
        this
            .userService
            .isAuthenticated()
            .subscribe(() => {
                this.helpPanelShown = !this.helpPanelShown;
            });
    }

    private setSelectedMainMenuItem(routeId: string): void {
        this.selectedMainMenuItem = lodash.find(
            this.mainMenuItems,
            (menuItem: TCIMainMenu) => lodash.startsWith(routeId, menuItem.getRouteId())
        );
    }

    private checkMenuTenantAvailability(menuItems: TCIMainMenu[]): Observable<TCIMainMenu[]> {
        return Observable
            .of(menuItems)
            .flatMap(items => items)
            .filter(menuItem => {
                if (menuItem.isAlwaysAvailable()) {
                    return true;
                } else {
                    let keep = false;
                    for (const tenant of Object.values(this.tenantMap)) {
                        keep = keep || menuItem.getTenantTypes()
                            .includes(tenant.getTenantType());
                    }
                    return keep;
                }
            })
            .toArray();
    }

    private watchForRouteChange(): void {
        this.routerEventsSubscription = this.router.events
            .subscribe(
                (event: Event) => {
                    if (event instanceof NavigationEnd) {
                        this.setSelectedMainMenuItem(
                            event
                                .urlAfterRedirects
                                .replace(/^\//, "")
                        );
                    } else if (event instanceof NavigationError) {
                        this.setSelectedMainMenuItem(
                            this
                                .location
                                .path(false)
                                .replace(/^\//, "")
                        );
                    }
                }
            );
    }
}
